import { useMutation, useQuery } from "@apollo/client/react";
import { RightOutlined, SnippetsOutlined } from "@ant-design/icons";
import { Button, Dropdown, Flex, Select, Space, Typography } from "antd";
import { useCallback, useId, useRef } from "react";
import { Link } from "react-router";
import { useEnterToNext } from "../../../hooks/useEnterToNext.ts";
import { useTranslation } from "react-i18next";
import {
  ListProducersDocument,
  UnitType,
  UpdateRecipeDocument,
  type UpdateRecipeInput,
} from "../../../gql/generated/graphqlSchema.tsx";
import { AllergenMultiSelect } from "../../../components/AllergenMultiSelect.tsx";
import { AutoSaveInput, AutoSaveTextArea } from "../../../components/AutoSaveInput.tsx";
import { LocaleNumberInput } from "../../../components/LocaleNumberInput.tsx";
import { QuantityUnitInput, type QuantityUnitValue } from "../../../components/QuantityUnitInput.tsx";
import { useRefreshState } from "../../../context/RefreshContext.tsx";
import { useOptionsWithCurrent } from "../../../hooks/useOptionsWithCurrent.ts";
import { useSaving } from "../../../hooks/useSaving.tsx";
import { useRecipe } from "../RecipeContext.tsx";
import { routes } from "../../../routes.ts";
import { formWide } from "../../../styles/shared.styles.ts";

const storagePresetKeys = [
  "ambient", // t("recipes.storagePresets.ambient")
  "coolDry", // t("recipes.storagePresets.coolDry")
  "coolDryDark", // t("recipes.storagePresets.coolDryDark")
  "refrigerate", // t("recipes.storagePresets.refrigerate")
  "refrigerateOpened", // t("recipes.storagePresets.refrigerateOpened")
  "frozen", // t("recipes.storagePresets.frozen")
] as const;

export const RecipeGeneralTab = () => {
  const { t } = useTranslation();
  const { recipe, setRecipe, units } = useRecipe();
  const [updateRecipe] = useMutation(UpdateRecipeDocument);
  const { trackSaving } = useSaving();
  const id = useId();
  const formRef = useRef<HTMLDivElement>(null);
  const enterToNext = useEnterToNext(formRef);

  const storagePresets = storagePresetKeys.map((key) => ({ key, label: t(`recipes.storagePresets.${key}`) }));
  const weightUnits = units.filter((u) => u.unitType !== UnitType.Volume);
  const { data: producersData } = useQuery(ListProducersDocument, {
    variables: { search: undefined, pagination: { offset: 0, limit: 100 } },
  });
  const producers = producersData?.producers.producers ?? [];
  const [selectedProducerId, setSelectedProducerId] = useRefreshState<string | null>(recipe.producerId ?? null);
  const producerOptions = useOptionsWithCurrent(producers, recipe.producer, (p) => ({
    label: p.companyName,
    value: p.id,
  }));

  const saveField = useCallback(
    (input: UpdateRecipeInput) => {
      trackSaving(() =>
        updateRecipe({
          variables: { id: recipe.id, input },
        }).then((result) => setRecipe(result.data?.updateRecipe.refreshedRecipe)),
      );
    },
    [recipe.id, updateRecipe, trackSaving, setRecipe],
  );

  const handleQuantityUnitChange =
    (field: "servingSize" | "netWeight") =>
    ({ quantity, unitId }: QuantityUnitValue) => {
      saveField({
        [`${field}Quantity`]: quantity,
        [`${field}UnitId`]: unitId,
      });
    };

  return (
    <Flex className={formWide} gap="middle" ref={formRef} vertical>
      <Space orientation="vertical" size="small">
        <Typography.Text strong>
          <label htmlFor={`${id}-name`}>{t("recipes.name")}</label>
        </Typography.Text>
        <AutoSaveInput
          classNames={{ input: "marker" }}
          defaultValue={recipe.name}
          id={`${id}-name`}
          onCommit={(v) => saveField({ name: v })}
          onPressEnter={enterToNext}
          placeholder={t("recipes.namePlaceholder")}
          showCaseToggle
        />
      </Space>
      <Space orientation="vertical" size="small">
        <Typography.Text strong>
          <label htmlFor={`${id}-nameForLabel`}>{t("recipes.nameForLabel")}</label>
        </Typography.Text>
        <AutoSaveInput
          classNames={{ input: "marker" }}
          defaultValue={recipe.nameForLabel ?? ""}
          id={`${id}-nameForLabel`}
          onCommit={(v) => saveField({ nameForLabel: v || null })}
          onPressEnter={enterToNext}
          placeholder={recipe.name}
          showCaseToggle
        />
        <Typography.Text type="secondary">{t("recipes.nameForLabelHelp")}</Typography.Text>
      </Space>
      <Flex gap={4} vertical>
        <Typography.Text strong>
          <label htmlFor={`${id}-allergens`}>{t("recipes.allergens")}</label>
        </Typography.Text>
        <AllergenMultiSelect
          id={`${id}-allergens`}
          onChange={(next) => saveField({ allergens: next })}
          placeholder={t("recipes.allergensPlaceholder")}
          value={recipe.allergens}
        />
        <Typography.Text type="secondary">{t("recipes.allergensHelp")}</Typography.Text>
      </Flex>
      <Space orientation="vertical" size="small">
        <Typography.Text strong>
          <label htmlFor={`${id}-desc`}>{t("recipes.description")}</label>
        </Typography.Text>
        <AutoSaveTextArea
          classNames={{ textarea: "marker" }}
          defaultValue={recipe.description ?? ""}
          id={`${id}-desc`}
          onCommit={(v) => saveField({ description: v || null })}
          rows={4}
        />
      </Space>
      <Space orientation="vertical" size="small">
        <Typography.Text strong>{t("recipes.netWeight")}</Typography.Text>
        <QuantityUnitInput
          inputClassName="marker"
          onChange={handleQuantityUnitChange("netWeight")}
          onPressEnter={enterToNext}
          units={weightUnits}
          value={{ quantity: recipe.netWeightQuantity ?? null, unitId: recipe.netWeightUnitId }}
        />
      </Space>
      <Space orientation="vertical" size="small">
        <Typography.Text strong>{t("recipes.servingSize")}</Typography.Text>
        <QuantityUnitInput
          inputClassName="marker"
          onChange={handleQuantityUnitChange("servingSize")}
          onPressEnter={enterToNext}
          units={weightUnits}
          value={{ quantity: recipe.servingSizeQuantity ?? null, unitId: recipe.servingSizeUnitId }}
        />
      </Space>
      <Space orientation="vertical" size="small">
        <Typography.Text strong>
          <label htmlFor={`${id}-spp`}>{t("recipes.servingsPerPackage")}</label>
        </Typography.Text>
        <LocaleNumberInput
          classNames={{ input: "marker" }}
          defaultValue={recipe.servingsPerPackage ?? undefined}
          id={`${id}-spp`}
          onCommit={(v) => saveField({ servingsPerPackage: v })}
          onPressEnter={enterToNext}
        />
      </Space>
      <Space orientation="vertical" size="small">
        <Flex align="center" gap="small" justify="space-between">
          <Typography.Text strong>
            <label htmlFor={`${id}-storage`}>{t("recipes.storageRequirements")}</label>
          </Typography.Text>
          <Dropdown
            destroyOnHidden
            menu={{
              items: storagePresets,
              onClick: ({ key }) => {
                const preset = storagePresets.find((p) => p.key === key);
                if (preset) saveField({ storageRequirements: preset.label });
              },
            }}
            trigger={["click"]}
          >
            <Button icon={<SnippetsOutlined />} size="small" type="link">
              {t("recipes.storagePresetsLabel")}
            </Button>
          </Dropdown>
        </Flex>
        <AutoSaveTextArea
          classNames={{ textarea: "marker" }}
          defaultValue={recipe.storageRequirements ?? ""}
          id={`${id}-storage`}
          onCommit={(v) => saveField({ storageRequirements: v || null })}
          rows={2}
        />
      </Space>
      <Flex gap={4} vertical>
        <Typography.Text strong>
          <label htmlFor={`${id}-producer`}>{t("recipes.producer")}</label>
        </Typography.Text>
        <Flex gap="small">
          <Select
            allowClear
            id={`${id}-producer`}
            onChange={(value: string | undefined) => {
              const next = value ?? null;
              setSelectedProducerId(next);
              saveField({ producerId: next });
            }}
            options={producerOptions}
            placeholder={t("recipes.producerPlaceholder")}
            style={{ flex: 1 }}
            value={selectedProducerId ?? undefined}
          />
          <Link to={selectedProducerId ? routes.producerDetail.to(selectedProducerId) : "#"}>
            <Button color="primary" disabled={!selectedProducerId} icon={<RightOutlined />} variant="outlined" />
          </Link>
        </Flex>
      </Flex>
    </Flex>
  );
};
